import { getLoggedinUser } from "../../api/apiCore";
import { logger } from "../../utils/logger";
import {
  TaskBoardConnectionOptions,
  TaskBoardConnectionState,
  TaskBoardWebSocketMessage,
  TaskBoardWebSocketError,
  ReconnectionConfig,
  WEBSOCKET_CLOSE_CODES,
  UnifiedMessage,
  MessageType,
} from "./types";

const DEFAULT_RECONNECTION_CONFIG: ReconnectionConfig = {
  maxAttempts: 5,
  initialDelay: 1000,
  maxDelay: 30000,
  backoffMultiplier: 2,
  jitter: true,
} as const;

export class TaskBoardWebSocketConnection {
  private socket: WebSocket | null = null;
  private state: TaskBoardConnectionState = "disconnected";
  private reconnectionAttempts = 0;
  private reconnectionTimer: NodeJS.Timeout | null = null;
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private options: TaskBoardConnectionOptions;
  private reconnectionConfig: ReconnectionConfig;
  private wsUrl: string;

  constructor(
    options: TaskBoardConnectionOptions,
    reconnectionConfig?: Partial<ReconnectionConfig>,
  ) {
    this.options = options;
    this.reconnectionConfig = {
      ...DEFAULT_RECONNECTION_CONFIG,
      ...reconnectionConfig,
    };
    this.wsUrl = this.buildWebSocketUrl();
  }

  private buildWebSocketUrl(): string {
    const user = getLoggedinUser();

    if (!user) {
      throw new TaskBoardWebSocketError(
        "User not authenticated",
        "AUTH_ERROR",
        false,
      );
    }

    const companyId = user.companyID || user.companyId;
    if (!companyId) {
      throw new TaskBoardWebSocketError(
        "Company ID not found",
        "COMPANY_ID_ERROR",
        false,
      );
    }

    if (companyId !== this.options.companyId) {
      throw new TaskBoardWebSocketError(
        "Company ID mismatch",
        "COMPANY_ID_MISMATCH",
        false,
      );
    }

    const baseUrl =
      process.env.REACT_APP_PROCESS_API_BASE_URL ||
      "https://dev-agent-proxy.jesss.ai";

    const wsBaseUrl = baseUrl.replace(/^https/, "wss").replace(/^http/, "ws");

    return `${wsBaseUrl}/api/agent/websocket/current-ai-tasks-state-browser-socket/${companyId}`;
  }

  private setState(newState: TaskBoardConnectionState): void {
    if (this.state !== newState) {
      this.state = newState;
      this.options.onStateChange?.(newState);
    }
  }

  private calculateReconnectionDelay(): number {
    const { initialDelay, maxDelay, backoffMultiplier, jitter } =
      this.reconnectionConfig;

    const baseDelay = Math.min(
      initialDelay * Math.pow(backoffMultiplier, this.reconnectionAttempts),
      maxDelay,
    );

    if (jitter) {
      const jitterAmount =
        1000 + this.reconnectionAttempts * 500 * Math.random();
      return baseDelay + jitterAmount;
    }

    return baseDelay;
  }

  private setupHeartbeat(): void {
    this.clearHeartbeat();

    this.heartbeatInterval = setInterval(() => {
      if (this.socket?.readyState === WebSocket.OPEN) {
        this.socket.send(
          JSON.stringify({
            type: "heartbeat",
            timestamp: new Date().toISOString(),
          }),
        );
      }
    }, 30000);
  }

  private clearHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private clearReconnectionTimer(): void {
    if (this.reconnectionTimer) {
      clearTimeout(this.reconnectionTimer);
      this.reconnectionTimer = null;
    }
  }

  private handleMessage = (event: MessageEvent): void => {
    try {
      const rawData = JSON.parse(event.data);

      // Handle heartbeat responses
      if (rawData.type === "heartbeat") {
        return;
      }

      if (this.isValidTaskMessage(rawData)) {
        this.options.onMessage(rawData as TaskBoardWebSocketMessage);
      } else {
        logger.warn("[TaskBoard WS] Invalid message format received:", rawData);
      }
    } catch (error) {
      const wsError = new TaskBoardWebSocketError(
        `Failed to parse message: ${error instanceof Error ? error.message : "Unknown error"}`,
        "MESSAGE_PARSE_ERROR",
      );
      this.options.onError?.(wsError);
    }
  };

  private isValidTaskMessage(data: any): boolean {
    return (
      data &&
      typeof data === "object" &&
      data.type === "tasks" &&
      Array.isArray(data.data) &&
      typeof data.count === "number" &&
      typeof data.companyId === "string" &&
      typeof data.timestamp === "number"
    );
  }

  private handleOpen = (): void => {
    logger.info("[TaskBoard WS] Connection established");
    this.setState("connected");
    this.reconnectionAttempts = 0;
    this.options.onConnectionChange?.(true);
    this.setupHeartbeat();
  };

  private handleClose = (event: CloseEvent): void => {
    const closeCodeMeaning = this.getCloseCodeMeaning(event.code);
    logger.debug(
      `[TaskBoard WS] Connection closed: ${event.code} (${closeCodeMeaning}) ${event.reason}`,
    );
    this.setState("disconnected");
    this.clearHeartbeat();
    this.options.onConnectionChange?.(false);

    if (
      event.code !== WEBSOCKET_CLOSE_CODES.NORMAL_CLOSURE &&
      this.reconnectionAttempts < this.reconnectionConfig.maxAttempts
    ) {
      this.scheduleReconnection();
    }
  };

  private getCloseCodeMeaning(code: number): string {
    const codes: Record<number, string> = {
      1000: "Normal Closure",
      1001: "Going Away",
      1002: "Protocol Error",
      1003: "Unsupported Data",
      1004: "Reserved",
      1005: "No Status Received",
      1006: "Abnormal Closure - Network/Server Issue",
      1007: "Invalid Data",
      1008: "Policy Violation",
      1009: "Message Too Big",
      1010: "Missing Extension",
      1011: "Server Error",
      1015: "TLS Handshake Error",
    };
    return codes[code] || `Unknown (${code})`;
  }

  private handleError = (event: Event): void => {
    logger.error("[TaskBoard WS] Connection error:", event);

    const wsError = new TaskBoardWebSocketError(
      "WebSocket connection error",
      "CONNECTION_ERROR",
    );

    this.setState("error");
    this.options.onError?.(wsError);
    this.options.onConnectionChange?.(false);
  };

  private scheduleReconnection(): void {
    if (this.reconnectionAttempts >= this.reconnectionConfig.maxAttempts) {
      logger.error("[TaskBoard WS] Max reconnection attempts reached");
      this.setState("error");
      return;
    }

    this.setState("reconnecting");
    this.reconnectionAttempts++;

    const delay = this.calculateReconnectionDelay();
    logger.debug(
      `[TaskBoard WS] Scheduling reconnection attempt ${this.reconnectionAttempts} in ${delay}ms`,
    );

    this.reconnectionTimer = setTimeout(() => {
      this.attemptConnection();
    }, delay);
  }

  private attemptConnection(): void {
    try {
      this.wsUrl = this.buildWebSocketUrl();
      logger.debug("[TaskBoard WS] Attempting connection");

      this.setState("connecting");
      this.socket = new WebSocket(this.wsUrl);

      this.socket.addEventListener("open", this.handleOpen);
      this.socket.addEventListener("message", this.handleMessage);
      this.socket.addEventListener("close", this.handleClose);
      this.socket.addEventListener("error", this.handleError);
    } catch (error) {
      const wsError =
        error instanceof TaskBoardWebSocketError
          ? error
          : new TaskBoardWebSocketError(
              `Failed to create WebSocket connection: ${error instanceof Error ? error.message : "Unknown error"}`,
              "CONNECTION_FAILED",
            );

      logger.error("[TaskBoard WS] Connection failed:", wsError.message);
      this.setState("error");
      this.options.onError?.(wsError);

      if (
        wsError.isRetryable &&
        this.reconnectionAttempts < this.reconnectionConfig.maxAttempts
      ) {
        this.scheduleReconnection();
      }
    }
  }

  public connect(): void {
    if (this.state === "connected" || this.state === "connecting") {
      return;
    }

    this.clearReconnectionTimer();
    this.reconnectionAttempts = 0;
    this.attemptConnection();
  }

  public disconnect(): void {
    logger.debug("[TaskBoard WS] Disconnecting");

    this.clearReconnectionTimer();
    this.clearHeartbeat();
    this.setState("disconnected");

    if (this.socket) {
      this.socket.removeEventListener("open", this.handleOpen);
      this.socket.removeEventListener("message", this.handleMessage);
      this.socket.removeEventListener("close", this.handleClose);
      this.socket.removeEventListener("error", this.handleError);

      if (
        this.socket.readyState === WebSocket.OPEN ||
        this.socket.readyState === WebSocket.CONNECTING
      ) {
        this.socket.close(
          WEBSOCKET_CLOSE_CODES.NORMAL_CLOSURE,
          "Client disconnect",
        );
      }

      this.socket = null;
    }
  }

  public getState(): TaskBoardConnectionState {
    return this.state;
  }

  public isConnected(): boolean {
    return (
      this.state === "connected" && this.socket?.readyState === WebSocket.OPEN
    );
  }

  public sendMessage<T>(message: T, type: MessageType): void {
    if (!this.isConnected()) {
      return;
    }

    const msg: UnifiedMessage<T> = {
      type,
      payload: message,
    };

    this.socket?.send(JSON.stringify(msg));
  }
}

export const connectToTaskBoardWebSocket = (
  options: TaskBoardConnectionOptions,
): TaskBoardWebSocketConnection => {
  const connection = new TaskBoardWebSocketConnection(options);
  connection.connect();
  return connection;
};
